/**
 * A Company portal's own sign-in page (ADR-024 rev 5, W5c; §10) —
 * Fias-rendered, the Company's brand by configuration, and no password field
 * anywhere:
 *
 *   - Sign in with a passkey: the way in. A passkey made here works only on
 *     this portal (the browser enforces it), and its fingerprint, face or PIN
 *     makes it two factors on its own.
 *   - Get a code by email: proves the address, once, to make a passkey — the
 *     first one for this portal, or a replacement for lost ones. A code never
 *     signs anyone in by itself.
 *   - Create an account: email and username (plus an invite code on an open
 *     portal; a members-only portal goes by the invitation the address holds)
 *     → a code → a passkey.
 *   - Continue with Fias: the fias.io hand-off — the way in for accounts with
 *     an authenticator app (they get no emailed code) and anyone who prefers it.
 *   - Join from an invitation (`/auth/join#<token>`, amendment 2026-10-27):
 *     the invitation email's one-time link already proved the address, so the
 *     page shows it, asks only for a username, and goes straight on to the
 *     passkey — one email, no code. The token is read from the fragment once,
 *     removed from the address bar, and only ever sent in a request body.
 *
 * Where the Company's owner chose emailed codes for this portal (amendment
 * 2026-10-30, `signInMethod: 'email_code'`) the page leads with "Email me a
 * code" and the code signs in by itself; a passkey already made for this site
 * is the second option, and none is offered. Sign-up and the join link end in
 * a code too. The server decides whether a code signed in: the verify reply is
 * either where to go or, if the owner switched back meanwhile, the passkey
 * step after all.
 *
 * The disclosure is fixed text the Company cannot change; signing in (or
 * entering the code) with it on screen is the consent. What a reply says never
 * depends on whether an account exists: "check your email" is the answer
 * either way.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/hooks/useAuth';
import { usePrefetchedPasskeyOptions } from '@/auth/hooks/usePrefetchedPasskeyOptions';
import { BootSplash } from '@/components/BootSplash';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, ApiError, type PortalSignInOptionsDto } from '@/lib/api';
import {
  PasskeyCancelled,
  createPasskeyCredential,
  getPasskeyAssertion,
  passkeysAvailable,
} from '@/lib/passkeys';
import { shareOrigin } from '@/lib/portal-host';
import { queryKeys } from '@/lib/query-keys';
import {
  PortalFrame,
  PortalSignIn,
  PortalUnavailable,
  portalSignInUrl,
  safeReturnTo,
} from './PortalPages';
import { PORTAL_DISCLOSURE_VERSION, PortalDisclosure } from './PortalDisclosure';
import { portalName, usePortalProfile, type PortalProfile } from './usePortalProfile';

type View =
  | { kind: 'start' }
  /** An invitation's join link proved this address: choose a username. */
  | { kind: 'join'; token: string; email: string }
  /** A join link that cannot be used (expired, used), or an address to finish on fias.io. */
  | { kind: 'link_problem'; message: ReactNode }
  /** Ask for an address: a code to make a passkey, or a new account. */
  | { kind: 'email'; mode: 'code' | 'sign_up' }
  | { kind: 'code'; email: string; mode: 'code' | 'sign_up' }
  /** The address is proven: make this portal's passkey. */
  | { kind: 'create'; recovery: boolean }
  /** The passkey exists: sign in with it. */
  | { kind: 'ready'; recovery: boolean };

function errorText(err: unknown): string {
  // The disclosure here is older than the one the server asks consent to: the
  // app is out of date.
  if (err instanceof ApiError && err.code === 'DISCLOSURE_REQUIRED') {
    return 'This page is out of date. Reload it, then sign in again.';
  }
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}

/** The S14 disclosure, plus what having a Fias account means. Fixed text. */
function Disclosure({ company }: { company: string }) {
  return (
    <p className="rounded-md border p-3 text-xs text-muted-foreground">
      <PortalDisclosure company={company} /> Your account is provided by Fias, and you can use it on
      fias.io too. By continuing you agree to the{' '}
      <a
        className="underline"
        href={`${shareOrigin()}/legal/terms`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Fias Terms
      </a>{' '}
      and{' '}
      <a
        className="underline"
        href={`${shareOrigin()}/legal/privacy`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Privacy Policy
      </a>
      .
    </p>
  );
}

function ContinueWithFias({ profile, returnTo }: { profile: PortalProfile; returnTo: string }) {
  if (!profile.signInPath) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
      </div>
      <Button asChild variant="outline" className="w-full h-11">
        <a href={portalSignInUrl(profile.signInPath, returnTo)}>Continue with Fias</a>
      </Button>
      <p className="text-xs text-muted-foreground">
        Use this if your account has an authenticator app, or to sign in on fias.io.
      </p>
    </div>
  );
}

function LinkButton({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button type="button" className="underline" onClick={onClick}>
      {children}
    </button>
  );
}

/** 32 random bytes, base64url — what an invitation's join link carries after `#`. */
const JOIN_TOKEN = /^[A-Za-z0-9_-]{43}$/;

function readJoinToken(): string | null {
  const raw = window.location.hash.replace(/^#/, '');
  return JOIN_TOKEN.test(raw) ? raw : null;
}

export function PortalSignInPage({
  initialMode = 'sign_in',
}: {
  initialMode?: 'sign_in' | 'sign_up' | 'join';
}) {
  const { profile, isLoading, isError, refetch } = usePortalProfile();
  const { isAuthenticated, isLoading: authLoading, retryBootProbe } = useAuth();
  const options = useQuery({
    queryKey: queryKeys.companyPortals.signInOptions(),
    queryFn: () => api.getPortalSignInOptions(),
    staleTime: 60_000,
    retry: 1,
  });
  const location = useLocation();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(
    (location.state as { from?: string } | null)?.from ?? params.get('returnTo') ?? '/',
  );

  // The server says "signed in" but this load does not know a user: either a
  // cached profile from just before the session ended, or a boot probe that
  // gave up waiting for the server's session refresh. Ask BOTH again — the
  // probe, then the profile — before concluding anything. A person the server
  // says is signed in is never shown the sign-in forms.
  const stale = Boolean(profile?.signedIn) && !authLoading && !isAuthenticated;
  const [reprobe, setReprobe] = useState<'idle' | 'running' | 'done'>('idle');
  useEffect(() => {
    if (!stale || reprobe !== 'idle') return;
    setReprobe('running');
    void retryBootProbe()
      .then(() => refetch())
      .finally(() => setReprobe('done'));
  }, [stale, reprobe, retryBootProbe, refetch]);

  if (isLoading || options.isLoading || authLoading) return <BootSplash />;
  if (isError || !profile) return <PortalUnavailable />;
  // Already signed in here — by BOTH accounts of it, never the cached profile
  // alone (which would bounce against the route guard): straight on.
  if (profile.signedIn && isAuthenticated) return <Navigate to={returnTo} replace />;
  if (stale && reprobe !== 'done') return <BootSplash />;
  if (stale) return <StillSigningIn />;
  // Signing in here not set up: the fias.io hand-off, as before W5c.
  if (options.isError || !options.data?.emailCode) return <PortalSignIn />;
  if (initialMode === 'join') {
    return <JoinFromLink profile={profile} options={options.data} returnTo={returnTo} />;
  }
  return (
    <SignInForms
      profile={profile}
      options={options.data}
      returnTo={returnTo}
      initialView={
        initialMode === 'sign_up' && options.data.signUp
          ? { kind: 'email', mode: 'sign_up' }
          : { kind: 'start' }
      }
    />
  );
}

/**
 * `/auth/join#<token>`: reads the invitation's join link once, takes it out of
 * the address bar (and so out of history, bookmarks and screenshots), asks the
 * server whose it is, and opens the forms at the right step.
 */
function JoinFromLink({
  profile,
  options,
  returnTo,
}: {
  profile: PortalProfile;
  options: PortalSignInOptionsDto;
  returnTo: string;
}) {
  const [token] = useState(readJoinToken);
  const [opened, setOpened] = useState<{ view: View; notice?: string } | null>(null);

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search,
      );
    }
    if (!token) {
      setOpened({ view: { kind: 'link_problem', message: LINK_NOT_VALID } });
      return;
    }
    let cancelled = false;
    api
      .previewPortalJoinLink(token)
      .then((preview) => {
        if (cancelled) return;
        if (preview.next === 'create_account') {
          setOpened({ view: { kind: 'join', token, email: preview.email } });
        } else if (preview.next === 'sign_in') {
          setOpened({
            view: { kind: 'start' },
            notice: alreadyHasAccount(preview.email, options.signInMethod === 'email_code'),
          });
        } else {
          setOpened({ view: { kind: 'link_problem', message: finishOnFias(preview.email) } });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setOpened({
          view: {
            kind: 'link_problem',
            message:
              err instanceof ApiError && err.code === 'JOIN_LINK_NOT_VALID'
                ? LINK_NOT_VALID
                : errorText(err),
          },
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token, options.signInMethod]);

  if (!opened) return <BootSplash />;
  return (
    <SignInForms
      profile={profile}
      options={options}
      returnTo={returnTo}
      initialView={opened.view}
      initialNotice={opened.notice}
    />
  );
}

const LINK_NOT_VALID =
  'This link has expired or was already used. You can still create your account or sign in here.';

function alreadyHasAccount(email: string, codeSignsIn: boolean): string {
  return codeSignsIn
    ? `${email} already has a Fias account. Sign in with a code by email.`
    : `${email} already has a Fias account. Sign in with your passkey, or get a code by email to make one for this site.`;
}

function finishOnFias(email: string): ReactNode {
  return (
    <>
      An account for {email} was started on fias.io but never finished. Finish creating it on{' '}
      <a className="underline" href={`${shareOrigin()}/auth/login`}>
        fias.io
      </a>
      , then come back here and sign in.
    </>
  );
}

/** What the email step says will happen next. */
function emailViewText(signingUp: boolean, byInvitation: boolean, codeSignsIn: boolean): string {
  const invitation = byInvitation ? 'Use the email address your invitation was sent to. ' : '';
  if (codeSignsIn) {
    return signingUp
      ? `${invitation}We will email you a code. Enter it here to finish.`
      : 'We will email you a code. Enter it here to sign in.';
  }
  return signingUp
    ? `${invitation}We will email you a code, then you will make a passkey for this site.`
    : 'We will email you a code. Then you will make a passkey for this site — it replaces any you had here before.';
}

/** The server says signed in, this load could not confirm it twice: a reload, never the forms. */
function StillSigningIn() {
  return (
    <div className="mx-auto max-w-md p-6 text-center">
      <p className="text-sm text-muted-foreground">
        You are signed in, but this page could not finish loading your session.
      </p>
      <Button className="mt-4" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  );
}

function SignInForms({
  profile,
  options,
  returnTo,
  initialView,
  initialNotice,
}: {
  profile: PortalProfile;
  options: PortalSignInOptionsDto;
  returnTo: string;
  initialView: View;
  initialNotice?: string;
}) {
  const company = options.companyName || portalName(profile);
  const site = window.location.hostname;
  const canUsePasskeys = options.passkeys && passkeysAvailable();
  // The owner chose emailed codes: a code signs in, and no passkey is offered.
  const codeSignsIn = options.signInMethod === 'email_code';
  const [view, setView] = useState<View>(initialView);
  const codeInput = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(initialNotice ?? null);

  // Ready before the click (see usePrefetchedPasskeyOptions).
  const signInOptions = usePrefetchedPasskeyOptions(
    async () => (await api.startPortalPasskeySignIn()).options,
    canUsePasskeys && (view.kind === 'start' || view.kind === 'ready'),
  );
  const creationOptions = usePrefetchedPasskeyOptions(
    async () => (await api.startPortalPasskeyRegistration()).options,
    canUsePasskeys && view.kind === 'create',
  );

  const go = (next: View) => {
    setError(null);
    setNotice(null);
    setView(next);
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (err) {
      if (err instanceof PasskeyCancelled) {
        setNotice(
          view.kind === 'create'
            ? 'The passkey was not made. Try again when you are ready.'
            : 'No passkey was used. If you have none for this site yet, get a code by email.',
        );
        // The next click gets fresh options, ready before it.
        if (view.kind === 'create') creationOptions.prefetch();
        else signInOptions.prefetch();
      } else if (err instanceof ApiError && err.code === 'ENROLLMENT_REQUIRED') {
        setError(err.message);
        setView({ kind: 'email', mode: 'code' });
      } else {
        setError(errorText(err));
        if (view.kind === 'create') creationOptions.prefetch();
        else if (view.kind === 'start' || view.kind === 'ready') signInOptions.prefetch();
      }
    } finally {
      setBusy(false);
    }
  };

  const signInWithPasskey = () =>
    run(async () => {
      const answer = await getPasskeyAssertion(await signInOptions.take());
      // The version this page shows — a consent is to the words on screen.
      const res = await api.finishPortalPasskeySignIn({
        response: answer,
        disclosureVersion: PORTAL_DISCLOSURE_VERSION,
        returnTo,
      });
      window.location.assign(safeReturnTo(res.redirect));
    });

  const sendCode = (e: FormEvent) => {
    e.preventDefault();
    if (view.kind !== 'email') return;
    const mode = view.mode;
    void run(async () => {
      // The server remembers which code it sent; the page never needs to know
      // whether the email already had an account.
      if (mode === 'sign_up') {
        await api.startPortalSignUp({
          email,
          username,
          ...(options.signUpByInvitation ? {} : { inviteCode }),
          returnTo,
        });
      } else {
        await api.startPortalEmailCode(email, returnTo);
      }
      setCode('');
      setView({ kind: 'code', email, mode });
    });
  };

  const verify = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await api.verifyPortalEmailCode(code.trim(), PORTAL_DISCLOSURE_VERSION);
      if ('redirect' in res) {
        window.location.assign(safeReturnTo(res.redirect));
        return;
      }
      setView({ kind: 'create', recovery: res.recovery });
    });
  };

  const joinWithLink = (e: FormEvent) => {
    e.preventDefault();
    if (view.kind !== 'join') return;
    const { token, email: invited } = view;
    void run(async () => {
      try {
        const res = await api.joinPortalWithLink({
          token,
          username,
          // The version this page shows — a consent is to the words on screen.
          disclosureVersion: PORTAL_DISCLOSURE_VERSION,
          returnTo,
        });
        if (res.next === 'create_passkey') {
          setView({ kind: 'create', recovery: res.recovery });
        } else if (res.next === 'enter_code') {
          // A portal set to emailed codes: the account exists, and a code is on its way.
          setCode('');
          setView({ kind: 'code', email: invited, mode: 'code' });
        } else if (res.next === 'sign_in') {
          setView({ kind: 'start' });
          setNotice(alreadyHasAccount(invited, codeSignsIn));
        } else {
          setView({ kind: 'link_problem', message: finishOnFias(invited) });
        }
      } catch (err) {
        if (err instanceof ApiError && err.code === 'JOIN_LINK_NOT_VALID') {
          setView({ kind: 'link_problem', message: LINK_NOT_VALID });
          return;
        }
        throw err;
      }
    });
  };

  const makePasskey = () =>
    run(async () => {
      const credential = await createPasskeyCredential(await creationOptions.take());
      const res = await api.finishPortalPasskeyRegistration(credential);
      setView({ kind: 'ready', recovery: res.recovery });
    });

  // The code field takes focus when it appears, so keyboard and screen-reader
  // users land where the next thing to type is.
  useEffect(() => {
    if (view.kind === 'code') codeInput.current?.focus();
  }, [view.kind]);

  const messages = (
    <>
      {notice && (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </>
  );

  const noPasskeysHere = !canUsePasskeys && (
    <p className="rounded-md border p-3 text-sm text-muted-foreground">
      This browser cannot use passkeys. Open this site in a current browser on your phone or
      computer, or use Continue with Fias.
    </p>
  );

  if (view.kind === 'start' && codeSignsIn) {
    return (
      <PortalFrame brand={profile.brand} title={`Sign in to ${company}`}>
        <Button
          type="button"
          className="w-full h-11"
          disabled={busy}
          onClick={() => go({ kind: 'email', mode: 'code' })}
        >
          Email me a code
        </Button>
        {messages}
        <Disclosure company={company} />
        <div className="space-y-1 text-center text-sm">
          {canUsePasskeys && (
            <p>
              Have a passkey for this site?{' '}
              <LinkButton onClick={() => void signInWithPasskey()}>
                {busy ? 'Waiting for your passkey…' : 'Sign in with it'}
              </LinkButton>
            </p>
          )}
          {options.signUp && (
            <p>
              New here?{' '}
              <LinkButton onClick={() => go({ kind: 'email', mode: 'sign_up' })}>
                Create your account
              </LinkButton>
            </p>
          )}
        </div>
        <ContinueWithFias profile={profile} returnTo={returnTo} />
      </PortalFrame>
    );
  }

  if (view.kind === 'start') {
    return (
      <PortalFrame brand={profile.brand} title={`Sign in to ${company}`}>
        {noPasskeysHere}
        {canUsePasskeys && (
          <Button
            type="button"
            className="w-full h-11"
            disabled={busy}
            onClick={() => void signInWithPasskey()}
          >
            {busy ? 'Waiting for your passkey…' : 'Sign in with a passkey'}
          </Button>
        )}
        {messages}
        <Disclosure company={company} />
        {canUsePasskeys && (
          <div className="space-y-1 text-center text-sm">
            <p>
              No passkey for this site yet, or lost yours?{' '}
              <LinkButton onClick={() => go({ kind: 'email', mode: 'code' })}>
                Get a code by email
              </LinkButton>
            </p>
            {options.signUp && (
              <p>
                New here?{' '}
                <LinkButton onClick={() => go({ kind: 'email', mode: 'sign_up' })}>
                  Create your account
                </LinkButton>
              </p>
            )}
          </div>
        )}
        <ContinueWithFias profile={profile} returnTo={returnTo} />
      </PortalFrame>
    );
  }

  if (view.kind === 'join') {
    return (
      <PortalFrame brand={profile.brand} title={`Join ${company}`}>
        <form className="space-y-4" onSubmit={joinWithLink}>
          <p className="text-sm text-muted-foreground">
            {codeSignsIn
              ? 'Choose a username. Then we will email you a code to sign in — no password.'
              : 'Choose a username. Then you will make a passkey for this site — no password, and no code to wait for.'}
          </p>
          <div className="grid gap-1.5">
            <Label htmlFor="portal-join-email">Email</Label>
            <Input id="portal-join-email" type="email" value={view.email} readOnly disabled />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="portal-join-username">Username</Label>
            <Input
              id="portal-join-username"
              autoComplete="username"
              required
              autoFocus
              maxLength={64}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <Disclosure company={company} />
          {messages}
          <Button type="submit" className="w-full h-11" disabled={busy || !username.trim()}>
            {busy ? 'Creating your account…' : 'Continue'}
          </Button>
        </form>
      </PortalFrame>
    );
  }

  if (view.kind === 'link_problem') {
    return (
      <PortalFrame brand={profile.brand} title={`Join ${company}`}>
        <p role="status" className="text-sm text-muted-foreground">
          {view.message}
        </p>
        <div className="space-y-1 text-center text-sm">
          {options.signUp && (
            <p>
              <LinkButton onClick={() => go({ kind: 'email', mode: 'sign_up' })}>
                Create your account
              </LinkButton>
            </p>
          )}
          <p>
            <LinkButton onClick={() => go({ kind: 'start' })}>Sign in</LinkButton>
          </p>
        </div>
      </PortalFrame>
    );
  }

  if (view.kind === 'email') {
    const signingUp = view.mode === 'sign_up';
    return (
      <PortalFrame
        brand={profile.brand}
        title={
          signingUp
            ? `Join ${company}`
            : codeSignsIn
              ? 'Sign in with a code'
              : 'Get a code by email'
        }
      >
        <form className="space-y-4" onSubmit={sendCode}>
          <p className="text-sm text-muted-foreground">
            {emailViewText(signingUp, options.signUpByInvitation, codeSignsIn)}
          </p>
          <div className="grid gap-1.5">
            <Label htmlFor="portal-email">Email</Label>
            <Input
              id="portal-email"
              type="email"
              autoComplete="email"
              required
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {signingUp && (
            <>
              <div className="grid gap-1.5">
                <Label htmlFor="portal-username">Username</Label>
                <Input
                  id="portal-username"
                  autoComplete="username"
                  required
                  maxLength={64}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              {!options.signUpByInvitation && (
                <div className="grid gap-1.5">
                  <Label htmlFor="portal-invite">Invite code</Label>
                  <Input
                    id="portal-invite"
                    required
                    maxLength={64}
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                  />
                </div>
              )}
            </>
          )}
          <Disclosure company={company} />
          {messages}
          <Button type="submit" className="w-full h-11" disabled={busy || !email}>
            {busy ? 'Sending…' : signingUp ? 'Create account' : 'Email me a code'}
          </Button>
          {signingUp && (
            <p className="text-center text-sm">
              Already have a Fias account?{' '}
              <LinkButton onClick={() => go({ kind: 'email', mode: 'code' })}>
                {codeSignsIn ? 'Sign in with a code' : 'Get a code by email'}
              </LinkButton>
            </p>
          )}
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => go({ kind: 'start' })}
          >
            Back
          </Button>
        </form>
      </PortalFrame>
    );
  }

  if (view.kind === 'code') {
    return (
      <PortalFrame brand={profile.brand} title="Check your email">
        <form className="space-y-4" onSubmit={verify}>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {`If ${view.email} can use this site, we sent it a code. It can take a minute to arrive.`}
          </p>
          <div className="grid gap-1.5">
            <Label htmlFor="portal-code">Code</Label>
            <Input
              ref={codeInput}
              id="portal-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <Disclosure company={company} />
          {messages}
          <Button type="submit" className="w-full h-11" disabled={busy || code.length < 6}>
            {busy ? 'Checking…' : codeSignsIn ? 'Sign in' : 'Continue'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => go({ kind: 'email', mode: view.mode })}
          >
            Use a different email
          </Button>
        </form>
      </PortalFrame>
    );
  }

  if (view.kind === 'create') {
    return (
      <PortalFrame brand={profile.brand} title="Make a passkey for this site">
        <p className="text-sm text-muted-foreground">
          A passkey signs you in to <strong>{site}</strong> with your fingerprint, face or device
          PIN — no password. It works only on this site. If your phone or computer offers to save it
          to your password manager, say yes: then it is on your other devices too.
        </p>
        {view.recovery && (
          <p className="rounded-md border p-3 text-sm text-muted-foreground">
            This replaces the passkeys you had for this site, and signs you out on every device. We
            will email you, and {company} will be told.
          </p>
        )}
        {noPasskeysHere}
        {messages}
        {canUsePasskeys && (
          <Button
            type="button"
            className="w-full h-11"
            disabled={busy}
            onClick={() => void makePasskey()}
          >
            {busy ? 'Waiting for your device…' : 'Make a passkey'}
          </Button>
        )}
      </PortalFrame>
    );
  }

  // ready
  return (
    <PortalFrame brand={profile.brand} title="Your passkey is ready">
      <p className="text-sm text-muted-foreground">
        From now on, choose “Sign in with a passkey” on <strong>{site}</strong>. Use it once now to
        sign in.
      </p>
      {messages}
      <Disclosure company={company} />
      <Button
        type="button"
        className="w-full h-11"
        disabled={busy}
        onClick={() => void signInWithPasskey()}
      >
        {busy ? 'Waiting for your passkey…' : 'Sign in with your passkey'}
      </Button>
    </PortalFrame>
  );
}
